import type { Module } from "src/system/module.types.ts";
import { createModuleRoutePaths } from "src/system/createModuleRoutePaths.ts";
import type { ClientConfig } from "src/client-config/getClientConfig.ts";
import { camelToKebabCase, mapObjectToArray } from "@exonn/utils";

export const buildDefaultModuleRouteObjects = ({
  module,
  clientConfig,
}: {
  module: Module;
  clientConfig: ClientConfig;
}) => {
  const resource = module.resource;
  const routePaths = createModuleRoutePaths({
    resource: resource,
  });

  const disableCreate =
    typeof module.disableCreate === "function"
      ? module.disableCreate({
          clientConfig,
        })
      : module.disableCreate;

  const disableEdit =
    typeof module.disableEdit === "function"
      ? module.disableEdit({
          clientConfig,
        })
      : module.disableEdit;

  const listRoute = {
    index: true,
    lazy: async () => {
      const { createList } = await import("src/system/list/createList.tsx");
      return createList({
        resource,
      });
    },
  };

  const editRoute = getEditRoute({
    module,
    resource,
  });

  const createRoute = getCreateRoute({
    module,
    resource,
  });

  return [
    {
      path: routePaths.root,
      lazy: async () => {
        const { createModuleLayout } = await import(
          "src/system/layout/createModuleLayout.tsx"
        );
        return createModuleLayout({ resource });
      },
      children: [
        module.list?.routeObject ?? listRoute,
        ...(disableEdit ? [] : [editRoute]),
        ...(disableCreate ? [] : [module.create?.routeObject ?? createRoute]),
      ],
    },
  ];
};

const getCreateRoute = ({
  module,
  resource,
}: {
  module: Module;
  resource: string;
}) => {
  if (module.create?.routeObject) {
    return module.create?.routeObject;
  }

  const routePaths = createModuleRoutePaths({
    resource: resource,
  });
  if (module.formTabs) {
    return {
      path: routePaths.create.root,
      lazy: async () => {
        const { createFormLayoutRoute } = await import(
          "src/system/form/createFormLayoutRoute.tsx"
        );
        return createFormLayoutRoute({
          resource: resource,
          layoutForm: module.layoutForm,
          type: "create",
        });
      },

      children: mapObjectToArray(module.formTabs, (tab, key, tabIndex) => {
        const index = tabIndex === 0;
        if (index) {
          return {
            index,
            path: index
              ? undefined
              : routePaths.create.tab(camelToKebabCase(key)).root,
            lazy: async () => {
              const { createFormRoute } = await import(
                "src/system/form/createFormRoute.tsx"
              );
              return createFormRoute({
                resource: resource,
                layoutForm: module.layoutForm,
                type: "create",
              });
            },
          };
        }

        return {
          path: index
            ? undefined
            : routePaths.create.tab(camelToKebabCase(key)).root,
        };
      }),
    };
  }

  return {
    path: routePaths.create.root,
    lazy: async () => {
      const { createCreate } = await import(
        "src/system/create/createCreate.tsx"
      );
      return createCreate({
        resource,
      });
    },
  };
};

const getEditRoute = ({
  module,
  resource,
}: {
  module: Module;
  resource: string;
}) => {
  if (module.edit?.routeObject) {
    return module.edit?.routeObject;
  }

  const routePaths = createModuleRoutePaths({
    resource: resource,
  });

  if (module.formTabs) {
    return {
      path: routePaths.edit.root,
      lazy: async () => {
        const { createFormLayoutRoute } = await import(
          "src/system/form/createFormLayoutRoute.tsx"
        );
        return createFormLayoutRoute({
          resource: resource,
          layoutForm: module.layoutForm,
          type: "edit",
        });
      },
      children: mapObjectToArray(module.formTabs, (tab, key, tabIndex) => {
        const index = tabIndex === 0;
        if (index) {
          return {
            index,
            path: index
              ? undefined
              : routePaths.edit.tab(camelToKebabCase(key)).root,
            lazy: async () => {
              const { createFormRoute } = await import(
                "src/system/form/createFormRoute.tsx"
              );
              return createFormRoute({
                resource: resource,
                layoutForm: module.layoutForm,
                type: "edit",
              });
            },
          };
        }

        return {
          path: index
            ? undefined
            : routePaths.edit.tab(camelToKebabCase(key)).root,
          ...(tab.routeObject
            ? tab.routeObject
            : {
                lazy: async () => {
                  const { createFormRoute } = await import(
                    "src/system/form/createFormRoute.tsx"
                  );
                  return createFormRoute({
                    resource: resource,
                    layoutForm: false,
                    tab: key,
                    type: "edit",
                  });
                },
              }),
        };
      }),
    };
  }

  return {
    path: routePaths.edit.root,
    lazy: async () => {
      const { createEdit } = await import("src/system/edit/createEdit.tsx");
      return createEdit({
        resource,
      });
    },
  };
};
